import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpCemex, CmxHttpOptions } from '@cemex-core/angular-services-v8';
import { Statuses } from '@cemex/cmx-status-indicator-v4';
import { TranslationService, DateFormatterService, FormatterService } from '@cemex-core/angular-localization-v7';

import { FinancialDocumentsResponse } from './../models/financial-documents.dto';
import { FinancialDocument, FinancialDocumentStatus } from './../models/financial-document.model';

import { EndpointService } from './../../services/helpers/endpoint.service';
import { DateConverterService } from './../../services/helpers/dateConverter.service';
import { MonthSummaryDTO } from '../models/month-summary.dto';
import { FinancialDocumentV4DTO } from '../models/financial-document-v4.dto';

@Injectable()
export class FinancialDocumentsService {

  constructor(
    private http: HttpCemex,
    private endpointService: EndpointService,
    private dateConverterService: DateConverterService,
    private router: Router,
    private translation: TranslationService,
    private dateFormatter: DateFormatterService,
    private formatter: FormatterService,
  ) {
    //
  }

  /**
   * @description GET /financialdocuments Retrieve the debit and credit documents of a given combination
   * of legal entity and company.
   * @param customerCode The type code of a legal entity.
   * @param companyCode The code of a company.
   * @param status The letter designated to a status: A, B, C, O, P.
   * @param countryCode The country code of a legal entity.
   * @param dateFrom OPTIONAL The start date of the document date property of the financial documents.
   * @param dateTo OPTIONAL The end date of the document date property of the financial documents.
   * @param lastUpdate OPTIONAL The latest date for the miminum limit to get documents.
   * @param userType OPTIONAL The user type to filter the documents data.
   * @param numPage OPTIONAL The page number for pagination.
   * @param numRecords OPTIONAL The number of records per page.
   * @returns Observable
   */
  public getFinancialDocumentsv5(customerCode: string, companyCode: string, status: string, countryCode: string,
    dateFrom?: string, dateTo?: string, lastUpdate?: string, userType?: number, numPage?: number, numRecords?: number):
    Observable<FinancialDocument[]> {
    const endpoint = this.endpointService.getUrl('v5', 'arm', 'financialdocuments');

    let options = {
      params: {
        customerCode: customerCode ?? "",
        countryCode: countryCode?.trim() ?? "",
        companyCode: companyCode !== "0" ? companyCode : "",
        status: status ?? "",
      },
    } as CmxHttpOptions;

    if (dateFrom) options = { ...options, params: { ...options.params, dateFrom: dateFrom.toString() } };
    if (dateTo) options = { ...options, params: { ...options.params, dateTo: dateTo.toString() } };
    if (lastUpdate) options = { ...options, params: { ...options.params, lastUpdate: dateFrom.toString() } };
    if (userType) options = { ...options, params: { ...options.params, userType: userType.toString() } };
    if (numPage) options = { ...options, params: { ...options.params, numPage: numPage.toString() } };
    if (numRecords) options = { ...options, params: { ...options.params, numRecords: numRecords.toString() } };

    return this.http.get(endpoint, options).pipe(
      catchError((error) => {
        this.router.navigate(['invoices-payments', 'error'], { fragment: 'failed-request' });
        return throwError('GET Request failed...');
      }),
      map((response: FinancialDocumentsResponse) => {
        return this.handleDocumentsData(response);
      })
    );
  }
  /**
   * @description GET /financialdocuments Retrieve the debit and credit documents of a given combination
   * of legal entity and company.
   * @param customerCode The type code of a legal entity.
   * @param companyCode The code of a company.
   * @param status The letter designated to a status: A, B, C, O, P.
   * @param countryCode The country code of a legal entity.
   * @param dateFrom OPTIONAL The start date of the document date property of the financial documents.
   * @param dateTo OPTIONAL The end date of the document date property of the financial documents.
   * @param lastUpdate OPTIONAL The latest date for the miminum limit to get documents.
   * @param userType OPTIONAL The user type to filter the documents data.
   * @param numPage OPTIONAL The page number for pagination.
   * @param numRecords OPTIONAL The number of records per page.
   * @returns Observable
   */
  public getFinancialDocuments(customerCode: string, companyCode: string, status: string, countryCode: string,
    dateFrom?: string, dateTo?: string, lastUpdate?: string, userType?: number, numPage?: number, numRecords?: number):
    Observable<FinancialDocument[]> {
    const endpoint = this.endpointService.getUrl('v2', 'arm', 'financialdocuments');

    let options = {
      params: {
        customerCode: customerCode ?? "",
        countryCode: countryCode?.trim() ?? "",
        companyCode: companyCode !== "0" ? companyCode : "",
        status: status ?? "",
      },
    } as CmxHttpOptions;

    if (dateFrom) options = { ...options, params: { ...options.params, dateFrom: dateFrom.toString() } };
    if (dateTo) options = { ...options, params: { ...options.params, dateTo: dateTo.toString() } };
    if (lastUpdate) options = { ...options, params: { ...options.params, lastUpdate: dateFrom.toString() } };
    if (userType) options = { ...options, params: { ...options.params, userType: userType.toString() } };
    if (numPage) options = { ...options, params: { ...options.params, numPage: numPage.toString() } };
    if (numRecords) options = { ...options, params: { ...options.params, numRecords: numRecords.toString() } };

    return this.http.get(endpoint, options).pipe(
      catchError((error) => {
        return throwError('GET Request failed...');
      }),
      map((response: FinancialDocumentsResponse) => {
        return this.handleDocumentsData(response);
      })
    );
  }

  /**
   * @description PATCH /invoicereviewed Update the invoiceReviewd property of a document.
   */
  public reviewInvoice(documentId: number, flag: boolean, debitFlag: boolean): Observable<boolean> {
    const endpoint = this.endpointService.getUrl('v2', 'arm', 'invoicereviewed');
    const options = {
      documentId: documentId ?? "",
      invoiceReviewed: flag ?? "",
      debitFlag: debitFlag ?? ""
    } as CmxHttpOptions;

    return this.http.patch(endpoint, options).pipe(
      map((response) => {
        return true;
      }),
      catchError((error: any) => throwError(error.error || false)),);
  }

  /**
   * @description Modifies the api data to comply with the front end code.
   * @param aux The response object of the financial documents web api.
   * @returns An array of financial documents.
   */
  private handleDocumentsData(aux: FinancialDocumentsResponse): FinancialDocument[] {
    const selectedLanguage = this.translation.selectedLanguage;
    const format = selectedLanguage.formatDate;
    const daysNames = selectedLanguage.dayNames.split(',');
    const monthsNames = selectedLanguage.monthNames.split(',');
    const numberOfDecimals = selectedLanguage.decimalNumbersMoney;
    const thousandSeparator = selectedLanguage.thousandSeparator;
    const decimalSeparator = selectedLanguage.decimalSeparator;
    aux.documents.forEach((document, i) => {
      document.index = i;
      document.checked = false;
      document.relatedPurchaseOrders = [];
      document.relatedContracts = [];
      document.countryCode = document.countryCode.trim();
      document.currencyCode = document.currencyCode.trim();
      document.fiscalControlNumber = document.fiscalControlNumber ? document.fiscalControlNumber : '';
      document.disputeStatusCode = document.disputeStatusCode.trim();
      if (document.ppdDueDate) {
        document.ppdDueDateObj = this.dateConverterService.getDateObject(document.ppdDueDate);
        document.ppdDueDateObj.setHours(0);
        document.ppdDueDateObj.setMinutes(0);
        document.ppdDueDateObj.setSeconds(0);
        document.ppdDueDateObj.setMilliseconds(0);
        document.formattedPPDDueDate = this.dateFormatter.format(
          document.ppdDueDateObj, format, daysNames, monthsNames
        );
      }
      if (document.createDate) {
        document.createDateObj = this.dateConverterService.getDateObject(document.createDate);
        document.createDateObj.setHours(0);
        document.createDateObj.setMinutes(0);
        document.createDateObj.setSeconds(0);
        document.createDateObj.setMilliseconds(0);
        document.formattedCreateDate = this.dateFormatter.format(
          document.createDateObj, format, daysNames, monthsNames
        );
      }
      if (document.dueDate) {
        document.dueDateObj = this.dateConverterService.getDateObject(document.dueDate);
        document.dueDateObj.setHours(0);
        document.dueDateObj.setMinutes(0);
        document.dueDateObj.setSeconds(0);
        document.dueDateObj.setMilliseconds(0);
        document.formattedDueDate = this.dateFormatter.format(
          document.dueDateObj, format, daysNames, monthsNames
        );
      }
      if (document.disputeCompromiseDate) {
        document.disputeCompromiseDateObj = this.dateConverterService.getDateObject(document.disputeCompromiseDate);
        document.disputeCompromiseDateObj.setHours(0);
        document.disputeCompromiseDateObj.setMinutes(0);
        document.disputeCompromiseDateObj.setSeconds(0);
        document.disputeCompromiseDateObj.setMilliseconds(0);
        document.formattedDisputeCompromiseDate = this.dateFormatter.format(
          document.disputeCompromiseDateObj, format, daysNames, monthsNames
        );
      }
      document.formattedAmount = this.formatter.formatNumber(
        document.amount, numberOfDecimals, thousandSeparator, decimalSeparator
      );
      document.formattedBalance = this.formatter.formatNumber(
        document.balance, numberOfDecimals, thousandSeparator, decimalSeparator
      );

    });

    return aux.documents;
  }

  public getDocumentStatusType(document: FinancialDocument): Statuses {
    if (document.docStatusCode === undefined) {
      return Statuses.DISABLED;
    }
    if (document.debitFlag) { // document is debit
      if (document.docStatusCode.trim() === FinancialDocumentStatus.PaymentInProcess ||
        document.docStatusCode.trim() === FinancialDocumentStatus.ConfirmedByBank) { // after payment
        return Statuses.SUCCESS;
      } else if (document.docStatusCode.trim() === FinancialDocumentStatus.Closed) { // after payment
        return Statuses.INFO;
      } else if (document.docStatusCode.trim() === FinancialDocumentStatus.UnderVerification) { // after payment
        return Statuses.DISABLED;
      } else { // before payment
        if (document.daysOverDue >= 1) {
          return Statuses.ALERT;
        } else if (document.daysToDue >= 3) {
          return Statuses.SUCCESS;
        } else if (document.daysToDue >= 0 && document.daysToDue <= 2) {
          return Statuses.WARNING;
        }
      }
    } else { // document is credit
      if (document.balance < 0) {
        return Statuses.SUCCESS;
      } else {
        return Statuses.INFO;
      }
    }
    return Statuses.ALERT;
  }

  public getCalendarv5(countryCode: string, customerCode: string, companyCode: string, dateFrom: string, dateTo: string, status: 'C' | 'O'): Observable<MonthSummaryDTO> {
    const endpoint = this.endpointService.getUrl('v5', 'arm', 'financialdocuments/summary');
    const options = {
      params: {
        countryCode: countryCode ?? "",
        customerCode: customerCode ?? "",
        companyCode: companyCode ?? "",
        dateFrom: dateFrom ?? "",
        dateTo: dateTo ?? "",
        status: status ?? ""
      }
    } as CmxHttpOptions;

    return this.http.get(endpoint, options).pipe(
      map((response: MonthSummaryDTO) => response),
      catchError((error: any) => throwError(error.error || 'GET Request from Server error')),);
  }

  public getCalendar(countryCode: string, customerCode: string, companyCode: string, dateFrom: string, dateTo: string, status: 'C' | 'O'): Observable<MonthSummaryDTO> {
    const endpoint = this.endpointService.getUrl('v4', 'arm', 'financialdocuments/summary');
    const options = {
      params: {
        countryCode: countryCode ?? "",
        customerCode: customerCode ?? "",
        companyCode: companyCode ?? "",
        dateFrom: dateFrom ?? "",
        dateTo: dateTo ?? "",
        status: status ?? ""
      }
    } as CmxHttpOptions;

    return this.http.get(endpoint, options).pipe(
      map((response: MonthSummaryDTO) => response),
      catchError((error: any) => throwError(error.error || 'GET Request from Server error')),);
  }

  public getDocumentsv5(documentsId: number[]): Observable<FinancialDocumentV4DTO[]> {
    const endpoint = this.endpointService.getUrl('v5', 'arm', 'financialdocuments');
    const body = [];

    documentsId.map(d => {
      body.push({ documentId: d });
    });

    return this.http.post(endpoint, body).pipe(
      map((response: Response) => response),
      catchError((error: any) => throwError(error.error || 'POST Request from Server error'))
    )
  }

  public getDocuments(documentsId: number[]): Observable<FinancialDocumentV4DTO[]> {
    const endpoint = this.endpointService.getUrl('v4', 'arm', 'financialdocuments');
    const body = [];

    documentsId.map(d => {
      body.push({ documentId: d });
    });

    return this.http.post(endpoint, body).pipe(
      map((response: Response) => response),
      catchError((error: any) => throwError(error.error || 'POST Request from Server error'))
    )
  }
}



//     // ======================================================================================================================
//     // Web services
//     // ======================================================================================================================

//      // GET /financialdocuments
//      public getFinancialDocumentsPage( customer: any, company: any, status: any, countryCode: string,
//         dateFrom?: string, dateTo?: string, lastUpdate?: string, numPage?: any, numRecords?: any, userType?: number):
//      Promise<FinancialDocument[]> {
//         let dateFromParam = ( dateFrom ) ? ( '&dateFrom=' + dateFrom ) : '';
//         let dateToParam = ( dateTo ) ? ( '&dateTo=' + dateTo ) : '';
//         let lastUpdateParam = ( lastUpdate ) ? '&lastUpdate=' + lastUpdate : '';
//         let $userType = (userType) ? '&userType=' + userType.toString() : '';
//         let url = 'financialdocuments?status=' + status +
//         '&customerCode=' + customer +
//         '&countryCode=' + countryCode +
//         '&numPage=' + numPage +
//         '&numRecords=' + numRecords +
//         lastUpdateParam + dateToParam + dateFromParam + $userType;
//         if( company !== '0' ){
//             url += ( '&companyCode=' + company );
//         }
//         return this.http.get( this.getUrl( url ) )
//         .map(
//             response => {
//                 let aux: FinancialDocumentDTO = response;
//                 return this.handleDocumentsData( aux );
//             }
//         )
//         .toPromise()
//         .then( data => { return data; } )
//         .catch( error => { return error; } );
//     }
//     // GET /opendeliveries
//     public getOpenDeliveries( society: any, accountClientCredit: any, days: any, countryCode: any, salesOrganization?:
//     string ): Promise<OpenDelivery> {
//         let api = 'opendeliveries?society=' + society +
//         '&accountClientCredit=' + accountClientCredit +
//         '&days=' + days +
//         '&countryCode=' + countryCode;
//         if( salesOrganization ) {
//             api +=  ( "&salesOrganization=" + salesOrganization );
//         }
//         return this.http.get( this.getUrl( api ) )
//         .map(
//             response => {
//                 let aux: OpenDelivery = response;
//                 aux.orders.forEach( order => {
//                     order.documentDateObj = this.getDateObject( order.documentDate );
//                 });
//                 return aux;
//             }
//         )
//         .toPromise()
//         .then( data => { return data; } )
//         .catch( error => { return error; } );
//     }

//     // GET /searchreference
//     public searchReference( docTypeCode: string, dateFrom: string, dateTo: string, customerCode: any, countryCode:string, searchText?:
//     any ): Promise<Array<SearchedReference>>{
//         let api = 'searchreference?documentTypeCode=' + docTypeCode +
//         '&dateFrom=' + dateFrom + '&dateTo=' + dateTo +
//         '&customerCode=' + customerCode + '&countryCode=' + countryCode;
//         if( searchText ) {
//             api += '&searchText=' + searchText;
//         }
//         return this.http.get( this.getUrl( api ) )
//         .map(
//             response => {
//                 let aux = response;
//                 if( aux !== undefined && aux.references !== undefined ){
//                     aux.references.forEach( reference => {
//                         reference.checked = false;
//                     });
//                 }
//                 return aux.references;
//             }
//         )
//         .toPromise()
//         .then( data => { return data; } )
//         .catch( error => { return error } );
//     }

//     // PUT /searchreference
//     public linkReference( customerDocsBlobId: number, documentTypeCode: string, references: Array<SearchedReference> ): Promise<boolean>{
//         let param = {
//             documentTypeCode: documentTypeCode,
//             customerBlobReqId: customerDocsBlobId,
//             references: references,
//         };
//         return this.http.put( this.getUrl( 'searchreference' ), param )
//         .map(
//             response => {
//                 return response.result;
//             }
//         )
//         .toPromise()
//         .then( data => { return data; } )
//         .catch( error => { return error; } );
//     };

//     // DELETE /references/{customerDocsBlobId}/clean
//     public deleteReferenceLink( customerDocsBlobId: number ): Promise<boolean> {
//         let api = 'references/' + customerDocsBlobId + '/clean';
//         return this.http.delete( this.getUrl( api ) )
//         .map( response => {
//             if( response.status === 200 ){
//                 return true;
//             }
//             else{
//                 return false;
//             }
//         })
//         .toPromise()
//         .then( data => { return data; } )
//         .catch( error => { return error; } );
//     }

//     // POST /ticketreport
//     public getTicketReport( customerCode: string, countryCode: string, documents: any,
//     jobsiteCode?: string, languageCode?: string, dateFrom?: string, dateTo?: string ): Promise<Array<Invoice>>{
//         let aux = {
//             customerCode: customerCode,
//             countryCode: countryCode,
//             documents: documents,
//             jobSiteCode: "",
//             dateFrom: "",
//             dateTo: "",
//             languageCode: "ES",
//         };
//         aux.jobSiteCode = ( jobsiteCode ) ? jobsiteCode : "";
//         aux.languageCode = ( languageCode ) ? languageCode : "ES";
//         aux.dateFrom = ( dateFrom ) ? dateFrom : "";
//         aux.dateTo = ( dateTo ) ? dateTo : "";
//         let api = 'ticketreport';
//         return this.http.post( this.getUrl( api ), aux )
//         .map(
//             response => {
//                 let aux: TicketReportDTO = response;
//                 return aux.invoices;
//             }
//         )
//         .toPromise()
//         .then( data => { return data; } )
//         .catch( error => { return error; } );
//     }

//     // GET /financialdocuments/{documentId}/relateddocuments/{documentType}
//     public getRelatedDocuments( documentId: number, docType: string,  ): Promise<Array<RelatedDocument>>{
//         let api = 'financialdocuments/' + documentId + '/relateddocuments/' + docType;
//         return this.http.get( this.getUrl( api ) )
//         .map(
//             response => {
//                 let aux: RelatedDocumentsDTO = response;
//                 return aux.codes;
//             }
//         )
//         .toPromise()
//         .then( data => { return data; } )
//         .catch( error => { return error; } );
//     }

//     // GET /financialdocuments/referencestypes
//     public getReferencesTypes(countryCode : string): Promise<Array<ReferenceType>> {
//         let url = 'financialdocuments/referencestypes?countryCode=' + countryCode;
//         return this.http.get( this.getUrl( url ) )
//         .map(
//             response => {
//                 let aux = response;
//                 return aux.documentsTypes;
//             }
//         )
//         .toPromise()
//         .then( data => { return data; } )
//         .catch( error => { return error; } );
//     }

//     // GET /salesorganizations
//     public getSalesOrganizations( roleCode: any ): Promise<salesOrganizations> {
//         let api = 'salesorganizations?roleCode=' + roleCode;
//         return this.http.get( this.getUrl( api ) )
//         .map(
//             response => {
//                 let aux = response;
//                 return aux;
//             }
//         )
//         .toPromise()
//         .then( data => { return data; } )
//         .catch( error => { return error; } );
//     }
// }
